/**
 * 🔴 Link safety. `ticket_link.url` is USER-SUPPLIED and may point anywhere (db/007's own note).
 *
 * The rule enforced here: a user-supplied URL may become an `href` or an `<img src>` and NOTHING
 * else. ⛔ No inline SVG, no <object>, no <embed>, no <iframe>, no background-image. Those all
 * execute or can be made to.
 *
 * ⚠️ `javascript:` is the obvious one and is not the only one. `data:` can carry an HTML document,
 * `blob:` can carry a script, and a scheme-relative `//evil` inherits the current scheme. The
 * allow-list below is therefore positive — anything not named is refused, so a scheme nobody thought
 * of is refused by default rather than permitted by default.
 */

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

export interface SafeUrl {
  ok: true;
  href: string;
  host: string;
}

export interface UnsafeUrl {
  ok: false;
  reason: string;
}

export function parseSafeUrl(raw: string | null | undefined): SafeUrl | UnsafeUrl {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { ok: false, reason: 'empty link' };
  }
  const trimmed = raw.trim();

  // Reject control characters outright: `java\nscript:` is a real bypass of naive prefix checks.
  if (/[ -]/.test(trimmed)) {
    return { ok: false, reason: 'link contains control characters' };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, reason: 'not a valid absolute URL' };
  }

  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) {
    return { ok: false, reason: `${parsed.protocol.replace(':', '')} links are not shown` };
  }

  return { ok: true, href: parsed.toString(), host: parsed.host };
}

export function isSafeUrl(raw: string | null | undefined): boolean {
  return parseSafeUrl(raw).ok;
}

/**
 * An SVG served over https is still an SVG: rendered in an <img> it cannot run script in our origin,
 * which is why images go in an <img> and never inline. This flags the case so the UI can say what it
 * is showing rather than pretending every image is a bitmap.
 */
export function looksLikeSvg(href: string): boolean {
  try {
    return new URL(href).pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
}
